/**
 * Générateur — Connaître et utiliser les priorités opératoires.
 * Attendu ID : 905 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 4)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « classe » — Ex.7 Une priorité (adapté au format fermé)
 *     Formats : texte (saisie, question a), qcm_unique (question b),
 *     texte_trou_drag (question c — la règle).
 *     Construction : expression a + b × c (b×c dans les tables), bon = a + bc,
 *     gauche-à-droite = (a+b)×c, toujours distincts (a(c−1) ≠ 0 par bornes).
 *     Effectifs : total − n1 − n2 ≥ 1 par construction.
 *     ⚠ Règle (question c) — ADAPTATION ANTI-AMBIGUÏTÉ validée : la phrase à
 *     4 trous accepterait la permutation « division/multiplication » (vraie
 *     mais comptée fausse au scoring positionnel). On donne UNE MOITIÉ de
 *     chaque paire ; l'échange des 2 trous restants rend la phrase FAUSSE.
 *     La question d du doc est absorbée par la carte « mental » (validé).
 *
 *   Carte « associer » — Ex.8 Associer chaque suite d'opérations à son résultat
 *     Format : relier (4 paires). 5 familles d'expressions à priorités ;
 *     résultats tous distincts par retirage borné + repli (paires du doc).
 *
 *   Carte « mental » — Ex.10 Calcul mental et priorités (+ question d Ex.7)
 *     Formats : texte_trou_saisie (PAIRE CONTRASTÉE : mêmes nombres avec/sans
 *     parenthèses — la force du doc), qcm_unique (distracteur « gauche à
 *     droite » calculé).
 *
 *   Carte « nino » — Ex.11 Nino et Aïssatou (analyse d'erreurs)
 *     Formats : tableau_vf, qcm_unique (le bon résultat).
 *     Construction de la COÏNCIDENCE (les deux erreurs donnent le même
 *     résultat faux) : recherche bornée de (a+u)(d−e) = (a′+u)d − e avec
 *     a = c·a′, b = c·u ; repli = les valeurs du doc (20, 4, 4, 8, 6 →
 *     42 = 42, bon = 162). Message : deux résultats identiques ne prouvent
 *     rien (contrôle de vraisemblance).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs, tex,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 905;

const L_CLASSE  = 'Le calcul de la classe (règle de priorité)';
const L_ASSOC   = 'Associer chaque calcul à son résultat';
const L_FRAC    = 'La barre de fraction';
const L_PAR     = 'Placer les parenthèses';
const L_PUI     = 'Une puissance dans le calcul';
const L_MENTAL  = 'Calcul mental et priorités';
const L_NINO    = "Nino et Aïssatou (analyse d'erreurs)";

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Dixièmes → écriture FR. */
const dixToFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Déduplique des choix de QCM (rotation par mélange des misconceptions). */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...shuffle(candidats), ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Le calcul de la classe
// ═══════════════════════════════════════════════════════════

const tirerClasse = () => {
    const a = randInt(2, 9);
    const b = randInt(3, 9);
    const c = randInt(3, 9);
    const bon = a + b * c;
    const gd = (a + b) * c;               // « gauche à droite » — toujours ≠ bon
    const total = randInt(22, 30);
    const n1 = randInt(8, 13);
    const n2 = randInt(6, Math.min(11, total - n1 - 1));
    return { a, b, c, bon, gd, total, n1, n2, autres: total - n1 - n2 };
};

const enonceClasse = ({ a, b, c, total, n1, n2, bon, gd }) =>
    `<p>Voici le calcul proposé aux <strong>${total} élèves</strong> d'une classe de 5e : ` +
    `<strong>${a} + ${b} × ${c}</strong>.</p>` +
    `<p>${n1} élèves ont trouvé <strong>${bon}</strong> et ${n2} élèves ont trouvé ` +
    `<strong>${gd}</strong>.</p>`;

// ── Carte « logique » : la question des effectifs (retour responsable —
// ce n'est pas un exercice de priorités : le calcul proposé est justement
// la DONNÉE INUTILE ; on travaille la lecture d'énoncé) ──

const genClasseQcm = () => {
    const t = tirerClasse();
    const surGd = randomChoice([true, false]);
    const cible = surGd ? t.gd : t.bon;
    const chxPriorites = `Ils ont respecté les priorités : ${t.b} × ${t.c} d'abord`;
    const chxGaucheDroite = `Ils ont fait une erreur : ils ont calculé de gauche à droite (${t.a} + ${t.b} d'abord)`;
    const bonne = surGd ? chxGaucheDroite : chxPriorites;
    const autres = [
        surGd ? chxPriorites : chxGaucheDroite,
        `Ils ont fait une erreur dans la table de ${t.c}`,
        'Ils ont fait une erreur : ils ont additionné tous les nombres',
    ];
    const texte = enonceClasse(t) +
        `<p>Qu'ont fait les élèves qui ont trouvé <strong>${cible}</strong> ?</p>`;
    return makeQcm([bonne, ...autres], bonne, texte, opts(L_CLASSE));
};

/**
 * La règle — une moitié de chaque paire en trou : l'échange des deux trous
 * rend TOUJOURS la phrase mathématiquement fausse (pas de permutation
 * équivalente — les formulations où mult./div. seraient interchangeables,
 * type « on effectue les {{multiplications}} et les {{divisions}} », sont
 * exclues d'office). Pool intrinsèquement borné (c'est une règle de cours) :
 * exemption d'unicité documentée dans coverage-targets.js.
 */
const VARIANTES_REGLE = [
    { phrase: 'La {{multiplication}} et la division sont prioritaires sur l\'{{addition}} et la soustraction.', pool: ['multiplication', 'division', 'addition', 'soustraction'] },
    { phrase: 'La multiplication et la {{division}} sont prioritaires sur l\'addition et la {{soustraction}}.', pool: ['multiplication', 'division', 'addition', 'soustraction'] },
    { phrase: 'Dans un calcul sans parenthèses, on effectue d\'abord les {{multiplications}} et les divisions, puis les {{additions}} et les soustractions.', pool: ['multiplications', 'divisions', 'additions', 'soustractions'] },
    { phrase: 'Dans un calcul sans parenthèses, on effectue d\'abord les multiplications et les {{divisions}}, puis les additions et les {{soustractions}}.', pool: ['multiplications', 'divisions', 'additions', 'soustractions'] },
];

/**
 * Les deux règles COURTES, ancrées dans un calcul.
 *
 * Elles s'écrivaient « La {{multiplication}} est prioritaire sur l'{{addition}}. » et
 * admettaient alors DEUX remplissages vrais : la division est prioritaire sur l'addition
 * tout autant que la multiplication, et un seul était accepté (retour prof 2026-09-25).
 * Le commentaire ci-dessus n'envisageait que l'échange des deux trous, pas le choix
 * d'une autre étiquette du stock.
 *
 * Nommer le calcul ferme la question : dans 3 + 4 × 5 il n'y a ni division ni
 * soustraction à effectuer, une seule étiquette convient à chaque trou.
 */
const REGLES_ANCREES = [
    () => {
        const [a, b, c] = [randInt(2, 9), randInt(2, 9), randInt(3, 9)];
        return {
            phrase: `Dans ${a} + ${b} × ${c}, on calcule d'abord la {{multiplication}}, `
                + 'puis l\'{{addition}}.',
            pool: ['multiplication', 'division', 'addition', 'soustraction'],
        };
    },
    () => {
        const c = randInt(2, 6);
        const b = c * randInt(2, 6);
        return {
            phrase: `Dans ${b + randInt(2, 9)} − ${b} ÷ ${c}, on calcule d'abord `
                + 'la {{division}}, puis la {{soustraction}}.',
            pool: ['multiplication', 'division', 'addition', 'soustraction'],
        };
    },
];

const genRegleDrag = () => {
    const v = randomChoice([...VARIANTES_REGLE, ...REGLES_ANCREES.map(f => f())]);
    const distracteurs = filtrerDistracteurs(v.phrase, v.pool);
    const texte = '<p>Complète la règle de priorité :</p>';
    return makeTexteTrou(v.phrase, texte, { ...opts(L_CLASSE), mode: 'drag', distracteurs });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Associer chaque calcul à son résultat
// ═══════════════════════════════════════════════════════════

/** 5 familles d'expressions à priorités — { txt, valFr } exacts. */
const FAMILLES_ASSOC = [
    () => {   // a + b × c
        const a = randInt(2, 9), b = randInt(2, 9), c = randInt(3, 9);
        return { txt: `${a} + ${b} × ${c}`, valFr: String(a + b * c) };
    },
    () => {   // a × b ÷ c  (divisible par construction : a = c × k)
        const c = randInt(2, 5), k = randInt(3, 8), b = randInt(2, 6);
        return { txt: `${c * k} × ${b} ÷ ${c}`, valFr: String(k * b) };
    },
    () => {   // a − b × c  (positif par construction)
        const b = randInt(2, 6), c = randInt(2, 6), r = randInt(2, 15);
        return { txt: `${b * c + r} − ${b} × ${c}`, valFr: String(r) };
    },
    () => {   // a − b × c + d
        const b = randInt(2, 7), c = randInt(2, 6), r = randInt(3, 12), d = randInt(2, 9);
        return { txt: `${b * c + r} − ${b} × ${c} + ${d}`, valFr: String(r + d) };
    },
    () => {   // x − y + z × 2  (décimaux A6)
        const y10 = randInt(31, 129);
        const diff10 = randInt(20, 80);
        let z10 = randInt(11, 49);
        if (z10 % 5 === 0) z10 += randomChoice([1, 2]);
        return {
            txt: `${dixToFr(y10 + diff10)} − ${dixToFr(y10)} + ${dixToFr(z10)} × 2`,
            valFr: dixToFr(diff10 + 2 * z10),
        };
    },
];


// ═══════════════════════════════════════════════════════════
// Carte « fraction » — niveau 4 : la barre de fraction, parenthèse implicite
// ═══════════════════════════════════════════════════════════

/**
 * Une barre de fraction groupe son numérateur : (a + b) ÷ c, écrit en fraction, se lit comme une
 * parenthèse. L'erreur visée n° 3 est de lire la barre comme un simple signe de division et de
 * calculer a + b ÷ c. Les deux valeurs sont toujours distinctes par construction (b ≠ b ÷ c parce
 * que c ≥ 2 et b ≥ 2), et la division est exacte : le numérateur est un multiple du dénominateur.
 */
const tirerFraction = () => {
    const c = randInt(2, 9);
    const q = randInt(2, 12);
    const somme = c * q;                       // numérateur, multiple exact de c
    const a = randInt(2, somme - 2);
    const b = somme - a;                       // a + b = somme, les deux ≥ 2
    return { a, b, c, valeur: q, piege: a + b / c };
};

const genFractionSaisie = () => {
    const t = tirerFraction();
    const texte = `<p>Voici une expression : $\\dfrac{${t.a} + ${t.b}}{${t.c}}$</p>`
        + '<p><strong>Calcule cette expression.</strong> La barre de fraction groupe le calcul '
        + 'du haut : elle joue le rôle d\'une parenthèse. Écris un nombre entier.</p>';
    const payload = makeSaisie(String(t.valeur), texte, opts(L_FRAC));
    payload._meta.params = {
        niveau: 4, expression: `(${t.a} + ${t.b}) ÷ ${t.c}`, reponse: t.valeur,
        erreurVisee: 'barre_lue_comme_division',
    };
    return payload;
};

const genFractionQcm = () => {
    const t = tirerFraction();
    // Le piège n'est proposé que s'il tombe juste : une valeur décimale se repérerait d'un coup d'œil.
    const piegeEntier = Number.isInteger(t.piege) && t.piege !== t.valeur;
    const choix = [String(t.valeur), String(t.a + t.b + t.c), String((t.a + t.b) * t.c)];
    const dis = [
        { valeur: String(t.a + t.b + t.c), code: 'gauche_a_droite' },
        { valeur: String((t.a + t.b) * t.c), code: 'operation_confondue' },
    ];
    if (piegeEntier) {
        choix.push(String(t.piege));
        dis.push({ valeur: String(t.piege), code: 'barre_lue_comme_division' });
    } else {
        choix.push(String(t.valeur + t.c));
        dis.push({ valeur: String(t.valeur + t.c), code: 'gauche_a_droite' });
    }
    const texte = `<p>Voici une expression : $\\dfrac{${t.a} + ${t.b}}{${t.c}}$</p>`
        + '<p><strong>Quelle est sa valeur ?</strong></p>';
    const payload = makeQcm(choix, String(t.valeur), texte, opts(L_FRAC));
    payload._meta.params = { niveau: 4, expression: `(${t.a} + ${t.b}) ÷ ${t.c}`, reponse: t.valeur, distracteurs: dis };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « parenthèses » — niveau 5 : placer les parenthèses pour que l'égalité soit vraie
// ═══════════════════════════════════════════════════════════

/**
 * Exclusion de la spec : tout placement autre que celui attendu — l'absence de parenthèse
 * comprise — doit rendre l'égalité FAUSSE, sans quoi la question aurait deux réponses. On le
 * vérifie en évaluant les deux écritures possibles avant de servir le tirage.
 */
const tirerParentheses = () => {
    for (let essai = 0; essai < 120; essai++) {
        const a = randInt(2, 12);
        const b = randInt(2, 12);
        const c = randInt(2, 9);
        const forme = randomChoice(['somme_produit', 'difference_produit']);
        const avec = forme === 'somme_produit' ? (a + b) * c : (a - b) * c;
        const sans = forme === 'somme_produit' ? a + b * c : a - b * c;
        if (forme === 'difference_produit' && a - b < 2) continue;
        if (avec === sans || avec < 2) continue;
        const signe = forme === 'somme_produit' ? '+' : '−';
        return { plate: `${a} ${signe} ${b} × ${c}`, attendue: `(${a} ${signe} ${b}) × ${c}`, valeur: avec, sans };
    }
    return { plate: '3 + 5 × 2', attendue: '(3 + 5) × 2', valeur: 16, sans: 13 };
};

const genParenthesesSaisie = () => {
    const t = tirerParentheses();
    const texte = `<p>Cette égalité est fausse telle qu'elle est écrite : `
        + `<strong>${t.plate} = ${t.valeur}</strong></p>`
        + '<p><strong>Recopie le calcul en plaçant une paire de parenthèses</strong> pour que '
        + 'l\'égalité devienne vraie. N\'écris que le calcul, sans le résultat.</p>';
    const payload = makeSaisie(t.attendue, texte, { ...opts(L_PAR), comparaison: 'expression' });
    payload._meta.params = {
        niveau: 5, expression: t.attendue, plate: t.plate, reponse: t.attendue,
        valeur: t.valeur, erreurVisee: 'parenthese_par_reflexe',
    };
    return payload;
};

const genParenthesesQcm = () => {
    const t = tirerParentheses();
    const choix = [t.attendue, t.plate];
    const dis = [{ valeur: t.plate, code: 'gauche_a_droite' }];
    // La parenthèse posée sur le produit : écriture naturelle chez qui parenthèse par réflexe,
    // mais elle ne change rien aux priorités, donc la valeur reste celle du calcul plat.
    const inutile = t.plate.replace(/(\d+) × (\d+)$/, '($1 × $2)');
    if (inutile !== t.plate && !choix.includes(inutile)) {
        choix.push(inutile);
        dis.push({ valeur: inutile, code: 'parenthese_par_reflexe' });
    }
    const enTrop = `(${t.plate})`;
    choix.push(enTrop);
    dis.push({ valeur: enTrop, code: 'parenthese_par_reflexe' });
    const texte = `<p>Voici un calcul : <strong>${t.plate}</strong></p>`
        + `<p><strong>Quelle écriture de ce calcul donne ${t.valeur} ?</strong></p>`;
    const payload = makeQcm(choix, t.attendue, texte, opts(L_PAR));
    payload._meta.params = { niveau: 5, expression: t.attendue, reponse: t.valeur, distracteurs: dis };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « puissance » — niveau 6 : une puissance simple dans l'expression
// ═══════════════════════════════════════════════════════════

/**
 * La puissance passe AVANT la multiplication : 3 × 4² vaut 3 × 16, jamais (3 × 4)². C'est
 * l'erreur visée n° 6. Exposants 2 et 3 seulement, bases entières, comme le borne la spec.
 */
const tirerPuissance = () => {
    const base = randInt(2, 6);
    const exposant = randomChoice([2, 3]);
    const k = randInt(2, 9);
    const pui = base ** exposant;
    const avant = randomChoice([true, false]);
    return {
        base, exposant, k, pui, avant,
        expression: avant ? `${k} × ${base}^${exposant}` : `${base}^${exposant} + ${k}`,
        tex: avant ? `${k} \\times ${base}^${exposant}` : `${base}^${exposant} + ${k}`,
        valeur: avant ? k * pui : pui + k,
        piege: avant ? (k * base) ** exposant : (base + k) ** exposant,
    };
};

const genPuissanceQcm = () => {
    const t = tirerPuissance();
    const choix = [String(t.valeur), String(t.piege), String(t.avant ? k2(t) : t.base * t.exposant + t.k)];
    const dis = [
        { valeur: String(t.piege), code: 'puissance_apres_multiplication' },
        { valeur: String(t.avant ? k2(t) : t.base * t.exposant + t.k), code: 'exposant_pris_pour_facteur' },
    ];
    const quatrieme = String(t.valeur + t.k);
    if (!choix.includes(quatrieme)) {
        choix.push(quatrieme);
        dis.push({ valeur: quatrieme, code: 'gauche_a_droite' });
    }
    const texte = `<p>Voici une expression : ${tex(t.tex)}</p>`
        + '<p><strong>Quelle est sa valeur ?</strong> Rappel : on calcule la puissance en premier.</p>';
    const payload = makeQcm(choix, String(t.valeur), texte, opts(L_PUI));
    payload._meta.params = { niveau: 6, expression: t.expression, reponse: t.valeur, distracteurs: dis };
    return payload;
};

/** L'exposant pris pour un facteur : base × exposant, puis l'opération. */
const k2 = (t) => t.k * t.base * t.exposant;

const genPuissanceSaisie = () => {
    const t = tirerPuissance();
    const texte = `<p>Voici une expression : ${tex(t.tex)}</p>`
        + '<p><strong>Calcule cette expression.</strong> Rappel : la puissance se calcule avant la '
        + 'multiplication et avant l\'addition. Écris un nombre entier.</p>';
    const payload = makeSaisie(String(t.valeur), texte, opts(L_PUI));
    payload._meta.params = {
        niveau: 6, expression: t.expression, reponse: t.valeur,
        erreurVisee: 'puissance_apres_multiplication',
    };
    return payload;
};

const genAssocierRelier = () => {
    for (let essai = 0; essai < 40; essai++) {
        const fams = shuffle([...FAMILLES_ASSOC]).slice(0, 4);
        const tirages = fams.map(f => f());
        const vals = tirages.map(t => t.valFr);
        const txts = tirages.map(t => t.txt);
        if (new Set(vals).size !== 4 || new Set(txts).size !== 4) continue;
        const pairs = tirages.map(t => ({ source: t.txt, target: t.valFr }));
        const texte = '<p>En respectant les priorités opératoires, relie chaque calcul à son résultat.</p>';
        return makeRelier(pairs, texte, opts(L_ASSOC));
    }
    // Repli déterministe : les paires du doc (résultats tous distincts)
    const pairs = [
        { source: '3 + 2 × 5', target: '13' },
        { source: '15 × 4 ÷ 3', target: '20' },
        { source: '19 − 4 × 4', target: '3' },
        { source: '17,7 − 11,7 + 0,3 × 2', target: '6,6' },
    ];
    return makeRelier(pairs, '<p>En respectant les priorités opératoires, relie chaque calcul à son résultat.</p>', opts(L_ASSOC));
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Calcul mental et priorités
// ═══════════════════════════════════════════════════════════

const LETTRES_MENTAL = ['G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];

/** Paires contrastées : mêmes nombres, avec/sans parenthèses. */
const PAIRES_MENTAL = [
    () => {   // (a + b) × c  vs  a + b × c
        const a = randInt(3, 9), b = randInt(2, 9), c = randInt(3, 6);
        return {
            l1: `(${a} + ${b}) × ${c}`, v1: String((a + b) * c),
            l2: `${a} + ${b} × ${c}`, v2: String(a + b * c),
        };
    },
    () => {   // k × (m − n)  vs  k × m − n
        const k = randInt(2, 6), n = randInt(2, 12), mn = randInt(3, 15);
        const m = n + mn;
        return {
            l1: `${k} × (${m} − ${n})`, v1: String(k * mn),
            l2: `${k} × ${m} − ${n}`, v2: String(k * m - n),
        };
    },
];

const genMentalTrous = () => {
    const p = randomChoice(PAIRES_MENTAL)();
    const [la, lb] = shuffle([...LETTRES_MENTAL]).slice(0, 2);
    const phrase =
        `${la} = ${p.l1} = {{${p.v1}}}\n` +
        `${lb} = ${p.l2} = {{${p.v2}}}`;
    const texte = '<p>Calcule mentalement, en respectant les priorités opératoires. ' +
        '<em>(Les deux calculs utilisent les mêmes nombres : compare-les !)</em></p>' +
        '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_MENTAL), mode: 'saisie' });
};

/** Gabarits « expression seule » pour le QCM — bonne + piège gauche-à-droite. */
const GABARITS_MENTAL_QCM = [
    () => {   // a + b × c — piège (a+b)×c
        const a = randInt(3, 9), b = randInt(2, 9), c = randInt(3, 6);
        return { expr: `${a} + ${b} × ${c}`, bonne: String(a + b * c), gd: String((a + b) * c) };
    },
    () => {   // k × m − n — piège k × (m−n)
        const k = randInt(2, 6), n = randInt(2, 12), m = n + randInt(3, 15);
        return { expr: `${k} × ${m} − ${n}`, bonne: String(k * m - n), gd: String(k * (m - n)) };
    },
    () => {   // a − (b + c) + d — piège « parenthèses ignorées » a − b + c + d
        const b = randInt(2, 8), c = randInt(2, 8), r = randInt(2, 12), d = randInt(2, 9);
        const a = b + c + r;
        return {
            expr: `${a} − (${b} + ${c}) + ${d}`,
            bonne: String(r + d),
            gd: String(a - b + c + d),
        };
    },
    () => {   // [e − (0,25 × 4 + f)] × g — 0,25 × 4 = 1 (table)
        const f = randInt(1, 5), r = randInt(2, 8), g = randInt(3, 9);
        const e = 1 + f + r;
        return {
            expr: `[${e} − (0,25 × 4 + ${f})] × ${g}`,
            bonne: String(r * g),
            gd: String(e - 1 - f),                      // oublie le × g final
        };
    },
    () => {   // x − y × 0,5 (question d de l'Ex.7) — piège (x − y) × 0,5
        const y = randInt(3, 15);
        const x = y + randInt(1, 8);
        return {
            expr: `${x} − ${y} × 0,5`,
            bonne: dixToFr(x * 10 - y * 5),
            gd: dixToFr((x - y) * 5),
        };
    },
];

const genMentalQcm = () => {
    const t = randomChoice(GABARITS_MENTAL_QCM)();
    const lettre = randomChoice(LETTRES_MENTAL);
    const distracteurs = dedupeChoix(t.bonne, [t.gd], [String(parseInt(t.bonne, 10) + 1), String(parseInt(t.bonne, 10) - 1)]);
    const texte = '<p>En respectant les priorités opératoires, calcule mentalement :</p>' +
        `<p><strong>${lettre} = ${t.expr}</strong></p>`;
    return makeQcm([t.bonne, ...distracteurs], t.bonne, texte, opts(L_MENTAL));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Nino et Aïssatou (analyse d'erreurs)
// ═══════════════════════════════════════════════════════════

const PRENOMS_DUO = [
    ['Nino', 'Aïssatou'], ['Sacha', 'Lina'], ['Mattéo', 'Fatou'], ['Iris', 'Yanis'],
];

/**
 * P = (a + b ÷ c) × d − e, avec a = c·a′ et b = c·u.
 * COÏNCIDENCE recherchée : Nino (étape 1 juste, puis multiplie par d−e) et le
 * second élève (additionne dans la parenthèse avant de diviser) trouvent le
 * MÊME résultat faux : (a+u)(d−e) = (a′+u)d − e.
 */
const tirerNino = () => {
    for (let i = 0; i < 300; i++) {
        const c = randInt(2, 5);
        const u = randInt(1, 4);
        const aPrime = randInt(3, 9);
        const a = c * aPrime;
        const b = c * u;
        const d = randInt(4, 9);
        const e = randInt(2, d - 1);
        const faux = (a + u) * (d - e);
        if (faux !== (aPrime + u) * d - e) continue;
        return { a, b, c, u, innerA: aPrime + u, d, e, faux, bon: (a + u) * d - e };
    }
    // Repli : les valeurs du doc — 42 = 42, bon = 162
    return { a: 20, b: 4, c: 4, u: 1, innerA: 6, d: 8, e: 6, faux: 42, bon: 162 };
};

/** Élision : « Calcul d'Iris » / « Calcul de Nino ». */
const de = (nom) => (/^[AEIOUÀÂÉÈÊÎÏÔÙ]/.test(nom) ? `d'${nom}` : `de ${nom}`);

/** Les deux calculs affichés comme sur la feuille : UNE ligne par étape. */
const enonceNino = (t, [p1, p2]) =>
    `<p>${p1} et ${p2} ont effectué le même calcul : ` +
    `<strong>P = (${t.a} + ${t.b} ÷ ${t.c}) × ${t.d} − ${t.e}</strong>. ` +
    'Ils ont trouvé le même résultat et pensent avoir bon.</p>' +
    `<p><strong>Calcul ${de(p1)}</strong> :<br>` +
    `P = (${t.a} + ${t.u}) × ${t.d} − ${t.e}<br>` +
    `P = ${t.a + t.u} × ${t.d - t.e}<br>` +
    `P = ${t.faux}</p>` +
    `<p><strong>Calcul ${de(p2)}</strong> :<br>` +
    `P = (${t.a + t.b} ÷ ${t.c}) × ${t.d} − ${t.e}<br>` +
    `P = ${t.innerA} × ${t.d} − ${t.e}<br>` +
    `P = ${t.innerA * t.d} − ${t.e}<br>` +
    `P = ${t.faux}</p>`;

const genNinoVF = () => {
    const t = tirerNino();
    const duo = randomChoice(PRENOMS_DUO);
    const [p1, p2] = duo;
    const bonResultat = randomChoice([true, false]);
    const lignes = [
        `La première étape ${de(p1)}, ${t.a} + ${t.b} ÷ ${t.c} = ${t.a + t.u}, est correcte`,
        `La première étape ${de(p2)}, (${t.a + t.b}) ÷ ${t.c} = ${t.innerA}, respecte les priorités`,
        'Deux résultats identiques prouvent que le résultat est juste',
        `Le bon résultat est ${bonResultat ? t.bon : t.faux}`,
    ];
    const bonnes = [0, 1, 1, bonResultat ? 0 : 1];
    const texte = enonceNino(t, duo) +
        '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>';
    return makeVraiFaux(lignes, bonnes, texte, opts(L_NINO));
};

const genNinoQcm = () => {
    const t = tirerNino();
    const duo = randomChoice(PRENOMS_DUO);
    const bonne = String(t.bon);
    // Pièges : le résultat commun des deux erreurs (majeur), « oublie le − e »,
    // et la piste d'Aïssatou poussée jusqu'au bout sans la soustraction.
    const distracteurs = dedupeChoix(bonne,
        [String(t.faux), String((t.a + t.u) * t.d), String(t.innerA * t.d)],
        [String(t.bon + 1), String(t.bon - 1)]);
    const texte = enonceNino(t, duo) +
        '<p>Quel est le <strong>bon</strong> résultat de P ?</p>';
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_NINO));
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    classe_qcm_unique:       genClasseQcm,
    classe_texte_trou_drag:  genRegleDrag,
    fraction_texte:          genFractionSaisie,
    fraction_qcm_unique:     genFractionQcm,
    parentheses_texte:       genParenthesesSaisie,
    parentheses_qcm_unique:  genParenthesesQcm,
    puissance_texte:         genPuissanceSaisie,
    puissance_qcm_unique:    genPuissanceQcm,
    associer_relier:         genAssocierRelier,
    mental_texte_trou_saisie: genMentalTrous,
    mental_qcm_unique:       genMentalQcm,
    nino_tableau_vf:         genNinoVF,
    nino_qcm_unique:         genNinoQcm,
};

export const CATEGORIES = [
    {
        id: 'classe', label: L_CLASSE,
        formatsList: [
            { key: 'classe_qcm_unique',      label: 'QCM' },
            { key: 'classe_texte_trou_drag', label: 'Drag' },
        ],
    },
    {
        id: 'fraction', label: L_FRAC,
        formatsList: [
            { key: 'fraction_texte', label: 'Saisie' },
            { key: 'fraction_qcm_unique', label: 'QCM' },
        ],
    },
    {
        id: 'parentheses', label: L_PAR,
        formatsList: [
            { key: 'parentheses_texte', label: 'Saisie' },
            { key: 'parentheses_qcm_unique', label: 'QCM' },
        ],
    },
    {
        id: 'puissance', label: L_PUI,
        formatsList: [
            { key: 'puissance_texte', label: 'Saisie' },
            { key: 'puissance_qcm_unique', label: 'QCM' },
        ],
    },
    {
        id: 'associer', label: L_ASSOC,
        formatsList: [{ key: 'associer_relier', label: 'Relier' }],
    },
    {
        id: 'mental', label: L_MENTAL,
        formatsList: [
            { key: 'mental_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'mental_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'nino', label: L_NINO,
        formatsList: [
            { key: 'nino_tableau_vf', label: 'Vrai/Faux' },
            { key: 'nino_qcm_unique', label: 'QCM' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    dixToFr, dedupeChoix, tirerClasse, FAMILLES_ASSOC, PAIRES_MENTAL,
    tirerFraction, tirerParentheses, tirerPuissance,
    GABARITS_MENTAL_QCM, tirerNino,
};
